/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import React, { memo, useMemo, useState, useEffect } from "react";
import Link from "next/link";
import { useQueries } from "@tanstack/react-query";
import { ArrowRight } from "lucide-react";
import ImageComponent from "@/components/ui/image";
import { PERSONAL_CURATION_COLLECTION_IDS } from "@/constants/product";
import { cn } from "@/utils/utils";
import { useMediaQuery } from "react-responsive";
import Carousel from "@/components/explore-sections/shared/carousel";

import HeroMain from "@/assets/images/v2/hero-main.webp";
import { getCollectionById } from "@/services/api/collections";

interface MappedGift {
  id: number | string;
  title: string;
  description: string;
  image: string;
  url: string;
}

const PersonalCurationSkeleton = memo(() => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
    {[...Array(4)].map((_, i) => (
      <div
        key={i}
        className="block animate-pulse md:bg-[#f7f3f24d] md:rounded-xl md:flex md:flex-col md:h-full md:overflow-hidden"
      >
        <div className="aspect-square md:aspect-[4/3] bg-[#e9e8e580] rounded-lg md:rounded-none mb-3 md:mb-0 relative" />
        <div className="md:flex md:flex-col md:flex-grow md:gap-4 md:p-6">
          <div className="bg-[#e9e8e580] rounded-md w-3/4 h-5 md:h-7 md:mb-3" />
          <div className="mt-2 md:mt-0 md:flex-grow space-y-1 md:space-y-2">
            <div className="bg-[#e9e8e580] rounded-md w-full h-3 md:h-4" />
            <div className="bg-[#e9e8e580] rounded-md w-5/6 h-3 md:h-4" />
          </div>
          <div className="bg-[#e9e8e580] rounded-md w-1/4 h-4 mt-2 hidden md:block" />
        </div>
      </div>
    ))}
  </div>
));

PersonalCurationSkeleton.displayName = "PersonalCurationSkeleton";

const CurationCard = memo(({ collection }: { collection: MappedGift }) => (
  <Link
    href={collection.url}
    className={cn(
      "group",
      "cursor-pointer no-underline block md:bg-[#f7f3f24d] md:rounded-xl md:flex md:flex-col md:h-full md:overflow-hidden transition-all duration-500 ease-out md:hover:shadow-[0_20px_25px_-5px_#005f520d,0_8px_10px_-6px_#005f520d]",
    )}
    aria-label={`Explore ${collection.title}`}
  >
    <div className="aspect-square md:aspect-[4/3] shadow-[0_1px_2px_0_#0000000d] md:shadow-none rounded-lg md:rounded-none mb-3 md:mb-0 relative overflow-hidden">
      <ImageComponent
        alt={collection.title}
        src={collection.image}
        width={350}
        height={420}
        className="object-cover w-full h-full transition-transform duration-500 md:duration-700 ease-out group-hover:scale-105 md:group-hover:scale-110"
        sizes="(min-width: 1024px) 25vw, (min-width: 640px) 50vw, 100vw"
        fallbackSrc={HeroMain}
      />
    </div>

    <div className="md:flex md:flex-col md:flex-grow md:p-6">
      <h3 className="font-lora tracking-tight md:tracking-normal text-[#005F52] md:mb-3 text-base md:text-xl font-medium leading-6 md:leading-7">
        {collection.title}
      </h3>
      <p className="font-outfit tracking-widest md:tracking-normal text-[#3e4946b3] md:text-[#3E4946] mt-1 md:mt-0 md:mb-6 text-[10px] md:text-sm font-normal lg:font-medium md:flex-grow leading-relaxed">
        {collection.description}
      </p>
      <div
        className={cn(
          "group/link",
          "font-lora uppercase tracking-wider text-[#735C00] items-center gap-2 text-sm font-bold leading-5 hidden md:inline-flex",
        )}
      >
        Explore{" "}
        <span className="transition-transform duration-150 ease-out group-hover/link:translate-x-1">
          <ArrowRight className="size-3.5" strokeWidth={2.5} />
        </span>
      </div>
    </div>
  </Link>
));

CurationCard.displayName = "CurationCard";

const PersonalCuration: React.FC = () => {
  const [isMounted, setIsMounted] = useState(false);
  const isMobile = useMediaQuery({ maxWidth: 767 }); // Grid uses md breakpoint (768px)

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const collectionQueries = useQueries({
    queries: PERSONAL_CURATION_COLLECTION_IDS.map((id) => ({
      queryKey: ["collection", id.toString()],
      queryFn: () => getCollectionById(id.toString()),
      staleTime: Infinity,
    })),
  });

  const isLoading = collectionQueries.some((query) => query.isLoading);

  const collectionData = useMemo(() => {
    return collectionQueries
      .map((query) => {
        const collection = query.data as any;
        if (!collection || !collection.slug || !collection.id) return null;
        return {
          id: collection.id.toString(),
          description: collection.metadata?.description || "",
          image: collection.media_url || "",
          url: `/listing/${collection.slug.toLocaleLowerCase()}?id=${collection.id}`,
          title: collection.name || "",
        };
      })
      .filter((item) => item !== null);
  }, [collectionQueries]);

  if (isLoading) {
    return (
      <section className="bg-[#FDF6F0] md:bg-[#FCF8F8] py-8 px-6 md:pt-16 md:pb-8 md:px-0 md:mb-0 relative">
        <div className="max-w-[36rem] md:max-w-[1440px] md:px-12 mx-auto">
          <div className="text-center max-w-3xl mb-12 md:mb-16 mx-auto">
            <div className="h-4 w-32 bg-v2-surface-container-high/50 rounded-md mx-auto mb-3 animate-pulse" />
            <div className="h-12 w-64 bg-v2-surface-container-high/50 rounded-md mx-auto mb-4 animate-pulse" />
            <div className="h-4 w-full bg-v2-surface-container-high/50 rounded-md mx-auto animate-pulse" />
          </div>
          <PersonalCurationSkeleton />
        </div>
      </section>
    );
  }
  return (
    <section className="bg-[#FFF0F2] md:bg-[#FCF8F8] py-8 px-6 md:pt-16 md:pb-8 md:px-0 md:mb-0 relative">
      <div className="max-w-[36rem] md:max-w-[1440px] md:px-12 mx-auto">
        <header className="text-center max-w-3xl mb-12 md:mb-16 mx-auto">
          <span className="font-outfit uppercase tracking-[0.3em] text-[#735C00] mb-3 text-[10px] font-bold">
            Personal Curation
          </span>
          <h2 className="text-center font-lora text-[#005F52] mb-2 md:mb-4 text-2xl md:text-4xl lg:text-5xl italic md:not-italic font-extralight md:font-semibold leading-8 md:leading-[1.25] lg:leading-none">
            <span
              className={cn(
                "tracking-tight md:tracking-normal not-italic md:italic font-bold md:font-normal",
                "md:hidden ",
              )}
            >
              Special Moments
            </span>
            <span className="hidden md:inline">
              <span className="tracking-tight md:tracking-normal not-italic md:italic font-bold md:font-normal">
                Special
              </span>{" "}
              Moments
            </span>
          </h2>
          <p className="font-outfit font-normal text-sm text-v2-on-surface-variant/80 uppercase tracking-widest mt-2">
            Made in India • Small batches
          </p>
        </header>

        {!isMounted || isMobile ? (
          <div className="grid grid-cols-2 gap-6 md:hidden">
            {collectionData.map((collection) => (
              <CurationCard key={collection.id} collection={collection} />
            ))}
          </div>
        ) : null}

        {isMounted && !isMobile ? (
          <div className="hidden md:block">
            <Carousel
              items={collectionData}
              itemsPerPage={4}
              renderItem={(collection) => (
                <div
                  key={collection.id}
                  className="flex-none w-[calc((100%-72px)/4)]"
                >
                  <CurationCard collection={collection} />
                </div>
              )}
            />
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default PersonalCuration;
